export type TournamentSources = "Q_LIVE" | "MANUAL";
export const TSourceDescriptions: Record<TournamentSources, string> = {
  Q_LIVE: "quizzing.live",
  MANUAL: "Manual",
};
export type TournamentTiers = "NATIONALS" | "REGIONALS" | "LOCAL" | "MISC";
export const TTierDescriptions: Record<TournamentTiers, string> = {
  NATIONALS: "Nationals", // Only BQF Nationals
  REGIONALS: "Regionals", // Regional Tournaments, Online Quizzing Championship
  LOCAL: "Local", // Local seasons, including local tournaments
  MISC: "Misc.", // NYE, Adult Quizzing, other one-offs
};
export const TTierRank: Record<TournamentTiers, number> = {
  NATIONALS: 3,
  REGIONALS: 2,
  LOCAL: 1,
  MISC: 0,
};
export const tiersInOrder: TournamentTiers[] = [
  "NATIONALS",
  "REGIONALS",
  "LOCAL",
  "MISC",
];
export const TTierDetails: Record<TournamentTiers, string> = {
  NATIONALS:
    "Nationals gets teams from all ministries and is held every year in various locations.",
  REGIONALS:
    "Regionals are tournaments with teams from several ministries, and are usually held late in the quiz season.",
  LOCAL:
    "Local seasons and tournaments provide regular season quizzing for most ministries.",
  MISC: "Miscellaneous events are one-offs and small tournaments, and are less competitive than other tournaments.",
};

export type TPageTypes =
  | "TEAM"
  | "QUIZZER"
  | "MINISTRY"
  | "TOURNAMENT"
  | "SERIES"
  | "REDIRECT"
  | "OTHER";
export const TPageTypeDescriptions: Record<TPageTypes, string> = {
  TEAM: "Team Page",
  QUIZZER: "Quizzer Page",
  MINISTRY: "Ministry Page",
  TOURNAMENT: "Tournament",
  SERIES: "Tournament Series",
  REDIRECT: "Redirect",
  OTHER: "Other/Misc Page",
};

export interface TournamentPageID {
  pageId: string;
  secondaryId: string; // like year of a tournament
}
export interface TournamentPageSummary extends TournamentPageID {
  pageType: TPageTypes;
  description?: string; // This will be auto-generated if it is not provided.
  trivia?: string[];
  timesPulled?: number;
  lastUpdated: string;
}
export interface TournamentPageDetails {
  additionalData: Record<string, string>; // misc stuff that is only grabbed in requests for this page
  alternateIds?: string[]; // Other IDs that this page can be known as
  deadLinks?: string[];
}
export type GenericTournamentPage = TournamentPageSummary &
  TournamentPageDetails;

export type TournamentPage =
  | Tournament
  | TeamPage
  | QuizzerPage
  | MinistryPage
  | RedirectPage
  | OtherPage
  | TournamentSeries;

// fields that apply to ministries, teams, and quizzers
interface PersonPageDetails extends GenericTournamentPage {
  // years this was active
  firstActive: number;
  lastActive?: number;
  notActive?: string;
}
interface CompetitorPageDetails extends PersonPageDetails {
  results?: TournamentResult[];
  headToHead?: HeadToHead[];
}

export interface OtherPage extends GenericTournamentPage {
  pageType: "OTHER";
  description: string; // only field required required
  currentTournament?: {
    tId: string;
    sId: string;
    team: string;
    teamId?: string;
    isCoach?: boolean;
  };
}
export interface TeamPage extends CompetitorPageDetails {
  pageType: "TEAM";
  teamName: string;
  ministry: string;
  achievements?: Achievement[];
  currentTournament?: {
    tId: string;
    sId: string;
    quizzers: string[];
    coach?: string;
  };
}
export interface QuizzerPage extends CompetitorPageDetails {
  pageType: "QUIZZER";
  quizzerName: string;
  ministry: string;
  achievements?: Achievement[];
  currentTournament?: {
    tId: string;
    sId: string;
    team: string;
    teamId?: string;
    isCoach?: boolean;
  };
}
export interface CurrentTeam {
  team: string;
  teamId?: string;
}
export interface MinistryPage extends PersonPageDetails {
  pageType: "MINISTRY";
  ministryName: string;
  location: string;
  currentTeams?: {
    local?: CurrentTeam[];
    postseason?: CurrentTeam[];
  };
}
export interface RedirectPage extends GenericTournamentPage {
  pageType: "REDIRECT";
  redirectTo: {
    pageId: string;
    secondaryId?: string;
  };
}

export interface TournamentSeries extends GenericTournamentPage {
  pageType: "SERIES";
  tournaments: TournamentSummary[];
}
export interface TournamentSummary extends TournamentPageID {
  host: string;
  location: string;
  startDate: string;
  endDate: string;
  tier: TournamentTiers;
  teamCount: number;
  topPlacements: string[];
  alternateIds?: string[];
  isIndividual?: boolean; // Default false
}
export interface Tournament extends TournamentSummary, GenericTournamentPage {
  pageType: "TOURNAMENT";
  rosters?: Record<string, TRoster>; // <team name, team details>
  events: TEvent[];
  stats?: {
    quizzer: TQuizzerStats[];
    team: TStats[];
  };
}

export interface TEvent {
  eventName: string;
  quizzes: TQuiz[];
  date: string;
  description?: string;
  order?: number; // Used for sorting
  roundNames?: Record<string, string>; // Rename rounds from 1,2,etc
  blackLine?: number[];
}

export interface TQuiz {
  quizId: number;
  site: number;
  round: number;
  teams: TTeamBox[];
  livestream?: string; // link to livestreaming for the quiz
  qm?: string;
  isIndividual?: boolean;
  flag?: string;
}
// Used for a team in a quiz
export interface TTeamBox {
  teamName: string; // links to team id in roster
  index: number; // side of the quiz
  tech: number;
  e: number; // errors
  s: number; // score
  p: number; // Place: 1, 2, or 3
  quizzers: TQuizzerBox[]; // Array, but order doesn't matter
  seed?: number; // For the start of a bracket, how the team is in that spot
}
// Used for a quizzer on a team in a quiz
export interface TQuizzerBox {
  qId: number;
  p: number;
  e: number;
  f: number;
  t: number;
  // Used for individual quizzing
  score?: number;
  sort?: number;
}
export interface TRoster {
  teamId?: string; // Links to team pages, if different from team name
  teamName: string;
  ministry?: string;
  coach?: string;
  quizzers: Record<string, string>; // <quizzerId, quizzerName>
  quizzerIds?: Record<string, string>; // Mapping from quizzerId to a different ID to refer to the quizzer as
  picture?: string; // URL to picture location

  dnf?: string[]; // Array of quizzers who didn't quiz/finish
  achievementsExclude?: string[]; // Quizzers who quizzed but won't be included in achievements
  resultsExclude?: boolean; // Excludes the team and its quizzers from results
}

export interface TStats {
  name: string;
  points?: string | number;
  errors?: string | number;
  qCount?: number;
  prize?: number;
}
export interface TQuizzerStats extends TStats {
  team: string;
}

export interface Achievement {
  // Pointer and basic details of what the achievement was and where it was earned
  tournament: {
    tId: string;
    sId: string;
    tier: TournamentTiers;
    totalTeams: number;
    endDate: string;
  };
  // The place the quizzer or team got
  place: number;
  // If this is not a team page, the team the quizzer was on or team that earned the achievement for the ministry
  team?: {
    teamId: string;
    teamName: string;
  };
}

export interface PageSearch extends TournamentPageID {
  pageType: TPageTypes;
  description: string;
  timesPulled?: number;
}

export interface TournamentResult {
  tournamentId: string;
  secondaryId: string;
  tier: TournamentTiers;
  startDate: string;
  endDate: string;
  teamName?: string;
  teamId?: string;
  place?: number;
  totalTeams: number; // If 0, this is an individual tournament
  indivPlace?: number;
  totalQuizzers?: number;
  prize?: number;
}
export interface HeadToHead {
  date: string;
  tournamentId: string;
  secondaryId: string;
  event: string;
  eventOrder?: number;
  quizDescription: string; // Most likely round and site, but could be different with round names and such
  round: number;
  site: number;
  wasWon: boolean;
  // First element of the array is main competitor second is opponent
  quizResults: {
    teamName: string;
    points: number;
    errors: number;
    p: number;
  }[];
  indvResults?: { name: string; correct: number; errors: number }[]; // Only for quizzer
}
